import useAgents from "./use-agents";
import { axios } from "utilities/axios";
import { AxiosError } from "axios";
import { useQuery, UseQueryOptions } from "react-query";
import {
    IAgent,
    IAgentGetQueryParameters,
    IAgentsCount,
    IAgentsCountResponse,
    Route,
} from "@edgetier/watchtower-types";

/**
 * @param parameters Route filters.
 * @param url URL to get counts.
 */
const getAgentCounts = async <Parameters extends IAgentGetQueryParameters>(url: string, parameters: Parameters) => {
    const { data } = await axios.get<IAgentsCountResponse<string>>(url, { params: parameters });

    return data.items;
};
/**
 * Count the number of agents matching some conditions.
 * @param parameters Query parameters.
 * @param options    Request Query options.
 * @returns          React Query result.
 */
export const useAgentCounts = <T = IAgentsCount<any>[]>(
    parameters: IAgentGetQueryParameters,
    options: Omit<UseQueryOptions<IAgentsCount<any>[], AxiosError, T>, "queryKey" | "queryFn"> = {}
) => {
    const url = Route.AgentsCounts;

    return useQuery<IAgentsCount<any>[], AxiosError, T>(
        [url, parameters],
        () => getAgentCounts(url, parameters),
        options
    );
};

/**
 * Count agents returned by the agents route. This is not an optimal way of doing this but a count route doesn't yet
 * exist on the backend.
 * @param parameters Query parameters.
 * @param options    Request Query options.
 * @returns          React Query result.
 */
export const useAgentsCount = (
    parameters: IAgentGetQueryParameters = {},
    options: Omit<UseQueryOptions<IAgent[], AxiosError, number>, "queryKey" | "queryFn"> = {}
) => {
    return useAgents(parameters, { ...options, select: (agents) => agents.length });
};
